import { useCallback, useState } from 'react';
import { FormikErrors, FormikHelpers, useFormik } from 'formik';
import { isNil, omitBy } from 'lodash';
import { Button, DocumentUpload, Typography } from 'components';
import HazardIcon from 'assets/icons/hazard.svg?react';
import { useDocumentUpload } from 'hooks/webapi';
import styles from './UploadDocuments.module.scss';
import {
  validateFileType,
  validateMaxSize,
  validateNonEmpty,
} from 'utils/validations';
import { useTranslation } from 'react-i18next';

type Props = {
  onSuccess: () => void;
};

type FormikValues = {
  incomeProof: Blob | null;
  otherDocumentFirst: Blob | null;
  otherDocumentNext: Blob | null;
};

export default function UploadDocuments({ onSuccess }: Props) {
  const { t } = useTranslation();
  const { uploadAttachment, sendAttachmentsToIncomeProof } =
    useDocumentUpload();
  const [pending, setPending] = useState(false);

  const handleSubmit = useCallback(
    async (
      { incomeProof, otherDocumentFirst, otherDocumentNext }: FormikValues,
      formikHelpers: FormikHelpers<FormikValues>,
    ) => {
      const formDataIncomeProof = new FormData();
      const formDataOtherDocumentFirst = new FormData();
      const formDataOtherDocumentNext = new FormData();

      if (incomeProof) {
        formDataIncomeProof.append('type', 'incomeDocument');
        formDataIncomeProof.append('file', incomeProof);
      }

      if (otherDocumentFirst) {
        formDataOtherDocumentFirst.append('type', 'incomeDocumentOther');
        formDataOtherDocumentFirst.append('file', otherDocumentFirst);
      }

      if (otherDocumentNext) {
        formDataOtherDocumentNext.append('type', 'incomeDocumentOther');
        formDataOtherDocumentNext.append('file', otherDocumentNext);
      }

      try {
        setPending(true);
        const attachmentIds: number[] = [];

        if (incomeProof) {
          const response = await uploadAttachment(formDataIncomeProof);
          attachmentIds.push(response.id);
        }

        if (otherDocumentFirst) {
          const response = await uploadAttachment(formDataOtherDocumentFirst);
          attachmentIds.push(response.id);
        }

        if (otherDocumentNext) {
          const response = await uploadAttachment(formDataOtherDocumentNext);
          attachmentIds.push(response.id);
        }

        await sendAttachmentsToIncomeProof(attachmentIds);

        setPending(false);

        onSuccess?.();
      } catch {
        formikHelpers.setStatus('error');
        formikHelpers.setValues({
          incomeProof: null,
          otherDocumentFirst: null,
          otherDocumentNext: null,
        });
      }
    },
    [uploadAttachment, onSuccess],
  );

  const validate = useCallback(
    ({
      incomeProof,
      otherDocumentFirst,
      otherDocumentNext,
    }: FormikValues): FormikErrors<FormikValues> => {
      const errors: FormikErrors<FormikValues> = {};

      errors.incomeProof =
        validateNonEmpty(incomeProof) ??
        validateFileType(incomeProof) ??
        validateMaxSize(incomeProof);

      errors.otherDocumentFirst =
        validateFileType(otherDocumentFirst) ??
        validateMaxSize(otherDocumentFirst);

      errors.otherDocumentNext =
        validateFileType(otherDocumentNext) ??
        validateMaxSize(otherDocumentNext);

      return omitBy(errors, isNil);
    },
    [],
  );

  const formik = useFormik<FormikValues>({
    initialValues: {
      incomeProof: null,
      otherDocumentFirst: null,
      otherDocumentNext: null,
    },
    validate,
    onSubmit: handleSubmit,
  });

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const { name } = event.target;
      const file = event.target.files ? event.target.files[0] : null;
      formik.setFieldValue(name, file);
    },
    [formik],
  );

  const handleDelete = useCallback(
    (name: string) => {
      formik.setFieldValue(name, null);
    },
    [formik],
  );

  return (
    <form onSubmit={formik.handleSubmit} className={styles.form}>
      <div className={styles.uploads}>
        <DocumentUpload
          name="incomeProof"
          className={styles.upload}
          type="INCOME_PROOF"
          onChange={handleChange}
          isTouched={formik.touched.incomeProof}
          error={formik.errors.incomeProof}
          value={formik.values.incomeProof}
          onDelete={handleDelete}
          dataTest="income-proof"
          customTitle={t('document_upload:upload_button')}
        />

        <DocumentUpload
          name="otherDocumentFirst"
          className={styles.upload}
          type="OTHER_DOCUMENT"
          onChange={handleChange}
          isTouched={formik.touched.otherDocumentFirst}
          error={formik.errors.otherDocumentFirst}
          value={formik.values.otherDocumentFirst}
          onDelete={handleDelete}
          dataTest="other-document-first"
          customTitle={t('document_upload:upload_button_optional')}
          customSubtitle={t('document_upload:other_document_subtitle')}
        />

        <DocumentUpload
          name="otherDocumentNext"
          className={styles.upload}
          type="OTHER_DOCUMENT"
          onChange={handleChange}
          isTouched={formik.touched.otherDocumentNext}
          error={formik.errors.otherDocumentNext}
          value={formik.values.otherDocumentNext}
          onDelete={handleDelete}
          dataTest="other-document-next"
          customTitle={t('document_upload:upload_button_optional')}
          customSubtitle={t('document_upload:other_document_subtitle')}
        />
      </div>

      {formik.status === 'error' && (
        <div className={styles['error-wrapper']}>
          <HazardIcon className={styles['error-icon']} />
          <Typography className={styles.error}>
            {t('error:validation.invalid_image')}
          </Typography>
        </div>
      )}

      <Button
        pending={pending}
        type="submit"
        className={styles.continue}
        disabled={
          !formik.values.incomeProof &&
          !formik.values.otherDocumentFirst &&
          !formik.values.otherDocumentNext
        }
      >
        {t('common:continue')}
      </Button>
    </form>
  );
}
